import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import { cva, cx } from 'cva';
import {
  Dialog as RACDialog,
  type DialogProps as RACDialogProps,
} from 'react-aria-components/Dialog';

import { Button } from '../button/button';
import { Form, type FormProps } from '../form/form';
import { Heading } from '../heading/heading';
import { Icon } from '../icon/icon';
import { Text, type TextProps } from '../text/text';

import styles from './dialog.module.css';

export {
  DialogTrigger,
  type DialogTriggerProps,
} from 'react-aria-components/Dialog';

export const dialogVariants = cva({
  base: styles.dialog,
  variants: {
    size: {
      xs: styles.size_xs,
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
      xl: styles.size_xl,
    },
  },
  defaultVariants: {
    size: 'm',
  },
});
export type DialogSize = 'xs' | 's' | 'm' | 'l' | 'xl';

/**
 * Dialog components.
 *
 * A set of components to create a dialog.
 * * `Dialog` - The main container of the dialog.
 * * `DialogForm` - A form wrapper with dialog-compatible flex layout.
 * * `DialogHeading` - The heading of the dialog. Can include a close icon.
 * * `DialogContent` - The layout/overflow container of the dialog content.
 * * `DialogContentText` - Styled text content within the dialog.
 * * `DialogActionButtons` - The action buttons of the dialog, such an confirm / cancel.
 *
 * The idea is to keep the dialog components separate so that they can be used in different ways.
 * Example usage of the Dialog components:
 *  * ```tsx
 * <Dialog role="alertdialog" isOpen={isOpen}>
 *  <DialogHeading variant="warning" isDismissible startIcon={<Icon name="deletion_delete" />}>
 *    Delete this item?
 *  </DialogHeading>
 *  <DialogContent>
 *    <DialogContentText>This action cannot be undone.</DialogContentText>
 *  </DialogContent>
 *  <DialogActionButtons>
 *    <Button>Cancel</Button>
 *    <Button>Confirm</Button>
 *  </DialogActionButtons>
 * </Dialog>
 * ```
 */

export type DialogProps = RACDialogProps &
  PropsWithChildren<{
    role?: 'alertdialog' | 'dialog';
    size?: DialogSize;
  }>;

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (props: DialogProps, ref) => {
    const { className, size, ...rest } = props;
    const classes = dialogVariants({ size, className });
    return (
      <RACDialog ref={ref} className={classes} {...rest}>
        {props.children}
      </RACDialog>
    );
  },
);

export type DialogFormProps = FormProps;

export const DialogForm = forwardRef<HTMLFormElement, DialogFormProps>(
  ({ className, ...props }, ref) => {
    return (
      <Form
        ref={ref}
        className={cx([styles.dialog_form, className])}
        {...props}
      />
    );
  },
);

export const dialogTitleVariants = cva({
  variants: {
    variant: {
      default: '',
      warning: styles.warning,
      destructive: styles.destructive,
    },
    startIconVariant: {
      default: styles.start_icon,
      warning: `${styles.start_icon} ${styles.warning_icon}`,
      destructive: `${styles.start_icon} ${styles.error_icon}`,
    },
  },
});

export type DialogHeadingProps = PropsWithChildren<{
  isDismissible?: boolean;
  startIcon?: ReactNode;
  variant?: 'default' | 'warning' | 'destructive';
  level?: 1 | 2 | 3 | 4 | 5 | 6;
}>;

export const DialogHeading = forwardRef<HTMLDivElement, DialogHeadingProps>(
  ({ children, isDismissible, startIcon, variant, level, ...props }, ref) => {
    const headingClassNames = dialogTitleVariants({ variant });
    const startIconClassNames = dialogTitleVariants({
      startIconVariant: variant,
    });

    return (
      <div ref={ref} {...props} className={styles.heading}>
        {startIcon && <div className={startIconClassNames}>{startIcon}</div>}
        <Heading
          level={level}
          slot="title"
          className={cx(styles.dialog_heading, headingClassNames)}
        >
          {children}
        </Heading>
        {isDismissible && (
          <Button
            icon={<Icon name="close" />}
            aria-label="Close"
            variant="tertiary"
            slot="close"
            round
            size="l"
            className={styles.close_button}
          />
        )}
      </div>
    );
  },
);

export type DialogContentProps = ComponentPropsWithoutRef<'div'>;

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (props, ref) => {
    const { children, className, ...rest } = props;
    const classes = cx([styles.dialog_content, className]);
    return (
      <div ref={ref} className={classes} {...rest}>
        {children}
      </div>
    );
  },
);

export type DialogContentTextProps = PropsWithChildren<{
  className?: string;
}> &
  TextProps;

export const DialogContentText = forwardRef<
  HTMLParagraphElement,
  DialogContentTextProps
>((props, ref) => {
  const { children, ...rest } = props;
  return (
    <Text elementType="p" color="high_emphasis" ref={ref} {...rest}>
      {children}
    </Text>
  );
});

export type DialogActionButtonsProps = PropsWithChildren<{
  className?: string;
}>;

export const DialogActionButtons = forwardRef<
  HTMLDivElement,
  DialogActionButtonsProps
>(({ children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={cx([className, styles.dialog_action_buttons])}
    >
      {children}
    </div>
  );
});
